/**
 * Selection chrome on the program monitor. Handles and the toolbar are DOM,
 * so they never get baked into the exported frame.
 */

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import type { ClipPlacement, TextClipStyle, TimelineClip, VideoProject } from '@canvas/shared-types';
import { FONT_FAMILIES } from '@/data/fontFamilies';
import {
  FONT_SIZE_CHOICES,
  PIP_CORNERS,
  boxFromPlacement,
  clipBox,
  flipPlacement,
  insetCrop,
  mediaContentSize,
  mergeCaptionEdit,
  movePlacement,
  pipPlacement,
  placementFromBox,
  resolvedPlacement,
  rotatePlacement,
  scaleFont,
  scalePlacement,
  type PipCorner,
  type TextMeasure,
} from '@/lib/video/clipFrame';
import type { Size } from '@/lib/video/frameGeometry';
import { cutoutImageRef } from '@/lib/video/placeMedia';
import { withReelCaption } from '@/lib/reel/reelCaption';
import { isClipProject, patchClip, restackClip, storyCaptionPageId } from '@/lib/video/timelineEdit';
import { useProjectStore } from '@/stores/projectStore';
import { useVideoStore } from '@/stores/videoStore';

interface ClipOverlayProps {
  frame: Size;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  onLive: (video: VideoProject) => void;
}

type MenuId = 'order' | 'flip' | 'pip' | 'more' | null;
type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'move' | 'rotate';

const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

function measureText(): TextMeasure {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  return (text, font) => {
    if (!ctx) return text.length * 10;
    ctx.font = font;
    return ctx.measureText(text || ' ').width;
  };
}

export default function ClipOverlay({ frame, canvasRef, onLive }: ClipOverlayProps): JSX.Element | null {
  const video = useVideoStore((s) => s.video);
  const time = useVideoStore((s) => s.currentTimeMs);
  const selectedId = useVideoStore((s) => s.selectedClipId);
  const measure = useRef<TextMeasure | null>(null);
  if (!measure.current) measure.current = measureText();
  const [menu, setMenu] = useState<MenuId>(null);
  const [cropping, setCropping] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<TimelineClip | null>(null);
  const typing = useRef(false);

  useEffect(() => {
    setMenu(null);
    setCropping(false);
    setEditing(false);
    setPreview(null);
    typing.current = false;
  }, [selectedId]);

  if (!isClipProject(video) || !selectedId) return null;
  const stored = video.clips.find((item) => item.id === selectedId);
  const clip = preview?.id === selectedId ? preview : stored;
  if (!clip || clip.kind === 'audio' || clip.kind === 'scene') return null;
  if (time < clip.startMs || time >= clip.startMs + clip.durationMs) return null;
  const box = clipBox(clip, frame, measure.current, time - clip.startMs);
  if (!box) return null;

  const textLike = clip.kind === 'text' || clip.kind === 'caption';
  const media = clip.kind === 'image' || clip.kind === 'video';
  const style = clip.kind === 'text' ? clip.text : null;
  const fontFamily = style?.fontFamily || clip.caption?.fontFamily || 'Arial, sans-serif';
  const fontSize = style?.fontSize || clip.caption?.fontSize || 42;
  const fill = style?.fill || clip.caption?.fill || '#ffffff';

  const scaleOf = (): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 1;
    return canvas.width / Math.max(1, canvas.clientWidth);
  };

  const livePatch = (next: VideoProject): void => {
    onLive(next);
  };

  const commit = (fn: (doc: VideoProject) => VideoProject, record = true): void => {
    setPreview(null);
    useVideoStore.getState().changeTimeline(fn, record ? undefined : { record: false });
  };

  const withPlacement = (doc: VideoProject, placement: ClipPlacement, text?: Partial<TextClipStyle>): VideoProject => {
    const current = doc.clips?.find((item) => item.id === clip.id);
    if (!current) return doc;
    if (current.kind === 'text' && current.text) {
      const nextText = { ...current.text, ...text, x: placement.x, y: placement.y };
      return patchClip(doc, clip.id, {
        placement,
        text: nextText,
        name: nextText.text.slice(0, 32) || current.name,
      });
    }
    if (current.kind === 'caption' && current.caption && text) {
      return patchClip(doc, clip.id, {
        placement,
        caption: mergeCaptionEdit(current.caption, {
          text: text.text,
          fill: text.fill,
          fontFamily: text.fontFamily,
          fontSize: text.fontSize,
        }),
      });
    }
    return patchClip(doc, clip.id, { placement });
  };

  const basePlacement = (): ClipPlacement => {
    if (clip.placement) return { ...clip.placement, lockAspect: clip.placement.lockAspect !== false };
    if (clip.kind === 'text' && clip.text) return placementFromBox(box, frame);
    if (clip.kind === 'caption') return placementFromBox(box, frame);
    return resolvedPlacement(clip, frame, mediaContentSize(clip));
  };

  const onHandle = (event: ReactPointerEvent<Element>, handle: Handle): void => {
    event.preventDefault();
    event.stopPropagation();
    const originX = event.clientX;
    const originY = event.clientY;
    const placement0 = basePlacement();
    const font0 = fontSize;
    const box0 = box;
    const crop0 = clip.image?.crop;
    let changed = false;
    const apply = (clientX: number, clientY: number, preview: boolean): void => {
      const scale = scaleOf();
      const dx = (clientX - originX) * scale;
      const dy = (clientY - originY) * scale;
      const rad = (-box0.rotation * Math.PI) / 180;
      const ldx = dx * Math.cos(rad) - dy * Math.sin(rad);
      const ldy = dx * Math.sin(rad) + dy * Math.cos(rad);
      const doc = useVideoStore.getState().video;
      if (!doc) return;
      const show = (patched: VideoProject): void => {
        setPreview(patched.clips?.find((item) => item.id === clip.id) ?? null);
        if (preview) livePatch(patched);
        else commit(() => patched, false);
      };
      if (handle === 'rotate') {
        const canvas = canvasRef.current?.getBoundingClientRect();
        if (!canvas) return;
        const cx = canvas.left + ((box0.x + box0.width / 2) / frame.width) * canvas.width;
        const cy = canvas.top + ((box0.y + box0.height / 2) / frame.height) * canvas.height;
        const start = Math.atan2(originY - cy, originX - cx);
        const angle = Math.atan2(clientY - cy, clientX - cx);
        const next = rotatePlacement(placement0, ((angle - start) * 180) / Math.PI);
        show(withPlacement(doc, next));
        return;
      }
      if (cropping && clip.kind === 'image' && handle !== 'move') {
        const edge = handle.includes('e') ? 'right' : handle.includes('w') ? 'left' : handle.includes('n') ? 'top' : 'bottom';
        const along = handle === 'n' || handle === 's' ? ldy : ldx;
        const delta = along / Math.max(box0.width, box0.height);
        const crop = insetCrop(crop0, edge, delta);
        show(patchClip(doc, clip.id, { image: clip.image ? { ...clip.image, crop } : clip.image }));
        return;
      }
      if (handle === 'move') {
        show(withPlacement(doc, movePlacement(placement0, dx, dy, frame)));
        return;
      }
      const dist0 = Math.max(12, Math.hypot(box0.width, box0.height) / 2);
      const dist1 = Math.max(12, Math.hypot(box0.width / 2 + ldx, box0.height / 2 + ldy));
      const factor = dist1 / dist0;
      if (textLike && (handle === 'e' || handle === 'w')) {
        const width = Math.max(0.08, (box0.width + (handle === 'e' ? ldx : -ldx)) / frame.width);
        show(withPlacement(doc, { ...placement0, width }));
        return;
      }
      if (textLike) {
        const nextFont = scaleFont(font0, factor);
        show(withPlacement(doc, scalePlacement(placement0, nextFont / Math.max(1, font0)), { fontSize: nextFont }));
        return;
      }
      show(withPlacement(doc, scalePlacement(placement0, factor)));
    };
    const onMove = (ev: PointerEvent): void => {
      if (!changed && Math.hypot(ev.clientX - originX, ev.clientY - originY) > 2) {
        changed = true;
        useVideoStore.getState().checkpoint();
      }
      if (changed) apply(ev.clientX, ev.clientY, true);
    };
    const onUp = (ev: PointerEvent): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (!changed) return;
      apply(ev.clientX, ev.clientY, false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const mirrorStoryCaption = (clipId: string, text: string): void => {
    const doc = useVideoStore.getState().video;
    const pageId = doc ? storyCaptionPageId(doc, clipId) : null;
    const project = useProjectStore.getState().currentProject;
    if (!pageId || !project?.pages) return;
    let changed = false;
    const pages = project.pages.map((page) => {
      if (page.pageId !== pageId) return page;
      const next = withReelCaption({ ...page, name: text.trim() || page.name }, text);
      if (next !== page) changed = true;
      return next;
    });
    if (!changed) return;
    const latest = useProjectStore.getState().currentProject;
    if (!latest) return;
    useProjectStore.getState().updateProject({ ...latest, pages });
  };

  const applyText = (patch: Partial<TextClipStyle>, record: boolean): void => {
    const doc = useVideoStore.getState().video;
    if (!isClipProject(doc)) return;
    const current = doc.clips.find((item) => item.id === clip.id);
    if (!current) return;
    let patched = doc;
    if (current.kind === 'text' && current.text) {
      const nextText = { ...current.text, ...patch };
      if (
        nextText.text === current.text.text &&
        nextText.fill === current.text.fill &&
        nextText.fontFamily === current.text.fontFamily &&
        nextText.fontSize === current.text.fontSize &&
        nextText.bar === current.text.bar &&
        nextText.background === current.text.background
      ) return;
      patched = patchClip(doc, clip.id, { text: nextText, name: nextText.text.slice(0, 32) || current.name });
    } else if (current.kind === 'caption' && current.caption) {
      const nextCaption = mergeCaptionEdit(current.caption, {
        text: patch.text,
        fill: patch.fill,
        fontFamily: patch.fontFamily,
        fontSize: patch.fontSize,
      });
      if (
        nextCaption.text === current.caption.text &&
        nextCaption.fill === current.caption.fill &&
        nextCaption.fontFamily === current.caption.fontFamily &&
        nextCaption.fontSize === current.caption.fontSize &&
        nextCaption.words === current.caption.words
      ) return;
      patched = patchClip(doc, clip.id, {
        caption: nextCaption,
        name: nextCaption.text.slice(0, 32) || current.name,
      });
    } else {
      return;
    }
    if (!record && !typing.current) {
      typing.current = true;
      useVideoStore.getState().checkpoint();
    }
    setPreview(null);
    useVideoStore.getState().changeTimeline(() => patched, record ? undefined : { record: false });
    onLive(useVideoStore.getState().video ?? patched);
    if (patch.text !== undefined) mirrorStoryCaption(clip.id, patch.text);
  };

  const setText = (patch: Partial<TextClipStyle>): void => {
    typing.current = false;
    applyText(patch, true);
  };

  const rotate90 = (): void => {
    commit((doc) => withPlacement(doc, rotatePlacement(basePlacement(), 90)));
    setMenu(null);
  };

  const flip = (axis: 'x' | 'y'): void => {
    commit((doc) => withPlacement(doc, flipPlacement(basePlacement(), axis)));
    setMenu(null);
  };

  const pip = (corner: PipCorner): void => {
    commit((doc) => withPlacement(doc, pipPlacement(basePlacement(), corner)));
    setMenu(null);
  };

  const order = (direction: 'forward' | 'backward'): void => {
    commit((doc) => restackClip(doc, clip.id, direction));
    setMenu(null);
  };

  const removeBackground = (): void => {
    if (!clip.image || busy) return;
    setBusy(true);
    setMenu(null);
    useVideoStore.getState().setNotice('Removing the background…');
    void cutoutImageRef(clip.image)
      .then((image) => {
        useVideoStore.getState().changeTimeline((doc) => patchClip(doc, clip.id, { image }));
        useVideoStore.getState().setNotice(null);
      })
      .catch((err: unknown) => {
        useVideoStore.getState().setNotice(err instanceof Error ? err.message : 'Could not remove the background.');
      })
      .finally(() => setBusy(false));
  };

  const unrot = `rotate(${-box.rotation}deg)`;

  return (
    <div className="vt-monitor-overlay">
      <div
        className="vt-sel"
        style={{
          left: `${(box.x / frame.width) * 100}%`,
          top: `${(box.y / frame.height) * 100}%`,
          width: `${(box.width / frame.width) * 100}%`,
          height: `${(box.height / frame.height) * 100}%`,
          transform: `rotate(${box.rotation}deg)`,
        }}
      >
        <div className="vt-sel-toolbar" style={{ transform: `translateX(-50%) ${unrot}` }}>
          {textLike ? (
            <>
              <button type="button" className="vt-sel-icon" title="Edit text" onClick={() => {
                setDraft(clip.kind === 'text' ? clip.text?.text ?? '' : clip.caption?.text ?? '');
                setEditing(true);
              }}>
                <PencilIcon />
              </button>
              <label className="vt-sel-color" title="Color">
                <input
                  type="color"
                  value={toHex(fill)}
                  onChange={(event) => setText({ fill: event.target.value })}
                />
              </label>
              <select
                className="vt-sel-select"
                aria-label="Font"
                value={fontFamily}
                style={{ fontFamily }}
                onChange={(event) => setText({ fontFamily: event.target.value })}
              >
                {FONT_FAMILIES.map((font) => (
                  <option key={font.family} value={font.family} style={{ fontFamily: font.family }}>
                    {font.label}
                  </option>
                ))}
                {!FONT_FAMILIES.some((font) => font.family === fontFamily) && (
                  <option value={fontFamily}>{fontFamily}</option>
                )}
              </select>
              <select
                className="vt-sel-select vt-sel-size"
                aria-label="Font size"
                value={String(fontSize)}
                onChange={(event) => setText({ fontSize: Number(event.target.value) })}
              >
                {!FONT_SIZE_CHOICES.includes(fontSize) && <option value={fontSize}>{fontSize}</option>}
                {FONT_SIZE_CHOICES.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </>
          ) : (
            <>
              {clip.kind === 'image' && (
                <button
                  type="button"
                  className={`vt-sel-icon ${cropping ? 'active' : ''}`}
                  title="Crop"
                  onClick={() => setCropping((on) => !on)}
                >
                  <CropIcon />
                </button>
              )}
              <button
                type="button"
                className={`vt-sel-icon ${clip.placement?.lockAspect === false ? '' : 'active'}`}
                title="Keep proportions"
                onClick={() => {
                  const current = basePlacement();
                  if (current.lockAspect === false) {
                    commit((doc) => withPlacement(doc, { ...current, lockAspect: true, height: undefined }));
                    return;
                  }
                  const content = mediaContentSize(clip);
                  const sized = boxFromPlacement(current, frame, content);
                  commit((doc) => withPlacement(doc, {
                    ...current,
                    lockAspect: false,
                    height: sized.height / frame.height,
                  }));
                }}
              >
                <LinkIcon />
              </button>
            </>
          )}
          <button type="button" className="vt-sel-icon" title="More" onClick={() => setMenu(menu === 'more' ? null : 'more')}>
            ···
          </button>
          {menu === 'more' && (
            <div className="vt-sel-menu">
              <button type="button" onClick={() => setMenu('order')}>Order</button>
              {media && <button type="button" onClick={rotate90}>Rotate by 90°</button>}
              {media && <button type="button" onClick={() => setMenu('flip')}>Flip</button>}
              {media && <button type="button" onClick={() => setMenu('pip')}>Picture in picture</button>}
              {clip.kind === 'image' && (
                <button type="button" disabled={busy} onClick={removeBackground}>Remove background</button>
              )}
              {clip.kind === 'text' && (
                <button
                  type="button"
                  onClick={() => {
                    setText({ bar: !clip.text?.bar, background: clip.text?.background || '#5b3a8c' });
                    setMenu(null);
                  }}
                >
                  {clip.text?.bar ? 'Remove text background' : 'Text background'}
                </button>
              )}
            </div>
          )}
          {menu === 'order' && (
            <div className="vt-sel-menu">
              <button type="button" onClick={() => order('forward')}>Bring forward</button>
              <button type="button" onClick={() => order('backward')}>Send backward</button>
            </div>
          )}
          {menu === 'flip' && (
            <div className="vt-sel-menu">
              <button type="button" onClick={() => flip('x')}>Flip horizontal</button>
              <button type="button" onClick={() => flip('y')}>Flip vertical</button>
            </div>
          )}
          {menu === 'pip' && (
            <div className="vt-sel-menu">
              {(Object.keys(PIP_CORNERS) as PipCorner[]).map((corner) => (
                <button key={corner} type="button" onClick={() => pip(corner)}>
                  {corner.replace('-', ' ')}
                </button>
              ))}
            </div>
          )}
        </div>
        {editing && (
          <textarea
            className="vt-sel-editor"
            style={{ transform: unrot }}
            value={draft}
            autoFocus
            onChange={(event) => {
              const value = event.target.value;
              setDraft(value);
              applyText({ text: value }, false);
            }}
            onBlur={(event) => {
              setEditing(false);
              applyText({ text: event.currentTarget.value }, false);
              typing.current = false;
            }}
          />
        )}
        {!editing && (
          <button type="button" className="vt-sel-body" aria-label="Move" onPointerDown={(event) => onHandle(event, 'move')} />
        )}
        {HANDLES.map((handle) => (
          <i
            key={handle}
            className={`vt-sel-handle vt-sel-${handle}`}
            onPointerDown={(event) => onHandle(event, handle)}
          />
        ))}
        <button
          type="button"
          className="vt-sel-rotate"
          title="Rotate"
          onPointerDown={(event) => onHandle(event, 'rotate')}
        >
          ↻
        </button>
      </div>
    </div>
  );
}

function toHex(color: string): string {
  if (/^#[0-9a-fA-F]{6}$/.test(color)) return color;
  return '#ffffff';
}

function PencilIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M2 12.5V14h1.5l7.8-7.8-1.5-1.5L2 12.5zM13.7 4.3a.7.7 0 0 0 0-1L12.2 1.8a.7.7 0 0 0-1 0l-1 1 1.5 1.5 1-1z" fill="currentColor" />
    </svg>
  );
}

function CropIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M4 1v3H1v2h3v8h2V6h8V4H6V1H4zm6 6v8h2v-3h3V10h-3V7h-2z" fill="currentColor" />
    </svg>
  );
}

function LinkIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M6.2 9.8a2.5 2.5 0 0 0 3.5 0l2-2a2.5 2.5 0 0 0-3.5-3.5l-.4.4 1.1 1.1.4-.4a1 1 0 0 1 1.4 1.4l-2 2a1 1 0 0 1-1.4 0l-.3-.3-1.1 1.1.3.2zM9.8 6.2a2.5 2.5 0 0 0-3.5 0l-2 2a2.5 2.5 0 0 0 3.5 3.5l.4-.4-1.1-1.1-.4.4a1 1 0 0 1-1.4-1.4l2-2a1 1 0 0 1 1.4 0l.3.3 1.1-1.1-.3-.2z" fill="currentColor" />
    </svg>
  );
}
